import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Paper, Typography, Box, CircularProgress, Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { getInstanceMonitorCbvRaw, getInstanceMonitorMsvRaw } from '../../services/apiService';

/**
 * RawCbvMsvModal — shows the raw plain-text response from getCBV.action or getMSV.action.
 * Port of RawGetCbvModal.tsx from highq-instance-monitor.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   instanceName: string,
 *   type: 'cbv' | 'msv',
 *   title?: string
 * }} props
 */
const RawCbvMsvModal = ({ open, onClose, instanceName, type = 'cbv', title }) => {
  const [rawText, setRawText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const modalTitle = title || (type === 'cbv' ? 'Raw getCBV Response' : 'Raw getMSV Response');
  const fileName = type === 'cbv' ? `getCBV_${instanceName}.txt` : `getMSV_${instanceName}.txt`;

  const fetchRaw = useCallback(async () => {
    if (!instanceName) return;
    setLoading(true);
    setError(null);
    try {
      const text = type === 'cbv'
        ? await getInstanceMonitorCbvRaw(instanceName)
        : await getInstanceMonitorMsvRaw(instanceName);
      setRawText(typeof text === 'string' ? text : JSON.stringify(text, null, 2));
    } catch (err) {
      setError(err.message || 'Failed to fetch raw response');
      setRawText('');
    } finally {
      setLoading(false);
    }
  }, [instanceName, type]);

  useEffect(() => {
    if (open && instanceName) {
      fetchRaw();
    }
    if (!open) {
      setRawText('');
      setError(null);
    }
  }, [open, instanceName, fetchRaw]);

  const handleCopy = async () => {
    if (!rawText) return;
    try {
      await navigator.clipboard.writeText(rawText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for older browsers
      const el = document.createElement('textarea');
      el.value = rawText;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (!rawText) return;
    const blob = new Blob([rawText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box>
          <Typography variant="h6" component="span">{modalTitle}</Typography>
          {instanceName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {instanceName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && !loading && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && rawText && (
          <Paper
            variant="outlined"
            sx={{
              bgcolor: '#1e293b',
              borderRadius: 0,
              borderColor: 'transparent',
              p: 2,
              maxHeight: 460,
              overflow: 'auto',
            }}
          >
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.78rem',
                color: '#e2e8f0',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                m: 0,
              }}
            >
              {rawText}
            </Typography>
          </Paper>
        )}

        {!loading && !error && !rawText && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, gap: 1 }}>
        <IconButton
          size="small"
          title="Refresh"
          onClick={fetchRaw}
          disabled={loading}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={handleCopy}
          disabled={!rawText || loading}
          variant="outlined"
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={!rawText || loading}
          variant="outlined"
        >
          Download
        </Button>
        <Button size="small" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RawCbvMsvModal;
